import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart, CART_CONTEXTS } from "../context/CartContext";
import { AuthContext } from "../App";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ShoppingBag, X, ArrowRight, AlertCircle, Lock } from "lucide-react";

// ============================================================
// CHECKOUT GATE RULES (HARD RULE — DO NOT REMOVE)
// ============================================================
// Brand browsing and cart access are public.
// Checkout is restricted to signed-in users only.
// No pricing, ownership, or checkout actions occur without authentication.
// ============================================================

// ============================================================
// READY-MADE BRAND CART DROPDOWN
// This component ONLY displays Ready-Made cart items
// It is HIDDEN on Create Brand pages to prevent context leakage
// ============================================================

// Routes where Ready-Made cart should be VISIBLE
const READY_MADE_CART_VISIBLE_ROUTES = [
  '/',
  '/store',
  '/dashboard'
];

// Routes where cart should NEVER appear (Create Brand flow)
const CART_HIDDEN_ROUTES = [
  '/activate-brand',
  '/brand/',
  '/create-brand',
  '/questionnaire'
];

const CartDropdown = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext) || {};
  const { 
    readyMadeCartItems, 
    removeFromReadyMadeCart, 
    getReadyMadeCartTotal, 
    getReadyMadeCartCount,
    activeCartContext,
    setLoginReturnUrl,
    CART_CONTEXTS: CONTEXTS
  } = useCart();
  
  const [isOpen, setIsOpen] = useState(false);
  const cartCount = getReadyMadeCartCount();

  // Check if we're on a Create Brand flow page (cart must be hidden)
  const isCreateBrandFlow = CART_HIDDEN_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );

  // Check if cart should be visible on current route
  const isOnAllowedRoute = READY_MADE_CART_VISIBLE_ROUTES.some(route => {
    if (route === '/') {
      return location.pathname === '/';
    }
    return location.pathname === route || location.pathname.startsWith(route + '/');
  }) && !location.pathname.includes('/store/success');

  // RULE: Ready-Made cart must NEVER render inside Create Brand UI
  if (isCreateBrandFlow) {
    return null;
  }

  // Only show on allowed routes
  if (!isOnAllowedRoute) {
    return null;
  }

  // Don't show Ready-Made cart dropdown if user is in Create Brand context
  // (prevents cart badge confusion)
  if (activeCartContext === CONTEXTS.CREATE_BRAND && cartCount === 0) {
    return null;
  }

  const handleCheckout = () => {
    setIsOpen(false);
    
    if (!user) {
      // Store return URL for after login - Always return to Ready-Made checkout
      setLoginReturnUrl();
      const redirectUrl = window.location.origin + '/store?checkout=ready-made';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } else {
      // If already on store page, scroll to checkout section
      if (location.pathname === '/store') {
        const checkoutSection = document.getElementById('checkout-section');
        if (checkoutSection) {
          checkoutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        // Navigate to store checkout
        navigate('/store?checkout=ready-made');
      }
    }
  };

  const handleSignInToCheckout = () => {
    setIsOpen(false);
    // Store return URL - Always return to Ready-Made store after login
    setLoginReturnUrl();
    const redirectUrl = window.location.origin + '/store?checkout=ready-made';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative p-2 hover:bg-[#F5F0EB]"
          data-testid="cart-dropdown-trigger"
        >
          <ShoppingBag className="w-5 h-5 text-[#78716C]" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C77D63] text-white text-xs font-bold rounded-full flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 p-4 bg-white rounded-xl shadow-lg border border-[#E6E1DB]"
      >
        {cartCount === 0 ? (
          <div className="text-center py-6">
            <ShoppingBag className="w-12 h-12 text-[#E6E1DB] mx-auto mb-3" />
            <p className="text-[#78716C] text-sm">Your cart is empty</p>
            <Button
              variant="ghost"
              onClick={() => {
                setIsOpen(false);
                navigate("/store");
              }}
              className="mt-3 text-[#C77D63] hover:text-[#B06A52] text-sm"
            >
              Browse Ready-Made Brands
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#2C332E]">Ready-Made Cart</h3>
              <span className="text-sm text-[#78716C]">
                {cartCount} item{cartCount !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {readyMadeCartItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-[#F5F0EB] rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#2C332E] text-sm truncate">
                      {item.brand?.name}
                    </p>
                    <p className="text-xs text-[#78716C]">
                      Ready-Made Brand Package
                      {item.experienceLevel && (
                        <span className="ml-1">
                          • {item.experienceLevel === "beginner" ? "🚀" : "📈"}
                        </span>
                      )}
                    </p>
                    {item.addOns?.length > 0 && (
                      <p className="text-xs text-[#8DA399] mt-1">
                        +{item.addOns.length} add-on
                        {item.addOns.length !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#2C332E] text-sm">
                      ${item.total?.toFixed(2)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromReadyMadeCart(item.brand?.id);
                      }}
                      className="p-1 text-[#78716C] hover:text-red-500 transition-colors"
                      aria-label="Remove from cart"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E6E1DB] mt-4 pt-4">
              <div className="flex items-center justify-between mb-4">
                <span className="font-semibold text-[#2C332E]">Total</span>
                <span className="text-xl font-bold text-[#2C332E]">
                  ${getReadyMadeCartTotal().toFixed(2)}
                </span>
              </div>

              {/* Cart Helper Text */}
              <p className="text-xs text-[#78716C] text-center mb-4">
                You may review and adjust your cart while signed out.<br />
                Checkout begins after sign-in.
              </p>

              {user ? (
                <Button
                  onClick={handleCheckout}
                  className="w-full bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={handleSignInToCheckout}
                    className="w-full bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full flex items-center justify-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Sign in to continue
                  </Button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-full text-sm text-[#78716C] hover:text-[#2C332E] transition-colors"
                  >
                    Continue browsing
                  </button>
                </div>
              )}

              {/* Trust Footnote */}
              <p className="text-xs text-[#78716C]/70 text-center mt-4 italic">
                Sign-in helps ensure each brand experience remains intentional and protected.
              </p>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CartDropdown;
